"use client";
import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";

export type UserRole = "student" | "teacher" | "country_admin" | "super_admin";

export type AuthUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
};

type AuthCtx = {
  user: AuthUser | null;
  token: string | null;
  login: (token: string, user: AuthUser) => void;
  logout: () => Promise<void>;
  isLoading: boolean;
};

const AuthContext = createContext<AuthCtx | null>(null);

export const LS_USER = "injaz_user";

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // apiFetch dispatches this when a protected request returns 401 (session
  // expired). Clear state and soft-redirect to /login — a client-side push
  // (not a full reload) so the staging Basic-Auth "site access" gate isn't
  // re-triggered. Skip if already on /login to avoid a redundant push.
  useEffect(() => {
    function onSessionExpired() {
      localStorage.removeItem(LS_USER);
      setUser(null);
      if (window.location.pathname !== "/login") router.replace("/login");
    }
    window.addEventListener("injaz:session-expired", onSessionExpired);
    return () => window.removeEventListener("injaz:session-expired", onSessionExpired);
  }, [router]);

  useEffect(() => {
    // SEC-04: auth is cookie-only; optimistically restore cached display info.
    const u = localStorage.getItem(LS_USER);
    if (u) {
      try {
        setUser(JSON.parse(u));
      } catch {
        localStorage.removeItem(LS_USER);
      }
    }

    // The cached user has no expiry, so it outlives the httpOnly cookie (12h)
    // and any logout from another tab — leaving the UI (e.g. the navbar's "My
    // Dashboard" button) showing logged-in while protected routes bounce to
    // /login. Verify against the server: /api/auth/me reads the cookie, so a
    // non-OK response means the session is gone — clear the stale local state.
    // Raw fetch (not apiFetch) so a 401 doesn't force-redirect public pages.
    const base = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
    let cancelled = false;
    fetch(`${base}/api/auth/me`, { credentials: "include" })
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          const { user: fresh } = (await res.json()) as { user: AuthUser };
          localStorage.setItem(LS_USER, JSON.stringify(fresh));
          setUser(fresh);
        } else {
          localStorage.removeItem(LS_USER);
          setUser(null);
        }
      })
      .catch(() => {
        // Network error — keep optimistic cached state, don't falsely log out.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = (_t: string, u: AuthUser) => {
    // Clear stale cached data (e.g. prior session's queries) before setting new state.
    queryClient.clear();
    // SEC-04: token is an httpOnly cookie set by the server — do not store in JS.
    // Cache user display info only.
    localStorage.setItem(LS_USER, JSON.stringify(u));
    setUser(u);
  };

  const logout = async () => {
    queryClient.clear();
    localStorage.removeItem(LS_USER);
    setUser(null);
    // Clear the server httpOnly cookie and AWAIT it: the caller redirects right
    // after, and if we don't wait the navigation cancels this request before
    // the cookie is cleared — middleware would then still see a valid token and
    // bounce the user straight back to their dashboard.
    try {
      await fetch(`${process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000"}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // best effort — proceed to redirect regardless
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
